
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import MonacoEditor from '@guolao/vue-monaco-editor';

  export default defineComponent({
    name: 'ads-brokerage-configuration-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
      MonacoEditor,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();

      const brokerName = ref('');
      const agentName = ref('');
      const slug = ref('');

      const titleEnContent = ref('{\n  "value": ""\n}');
      const titleArContent = ref('{\n  "value": ""\n}');
      const descriptionEnContent = ref('{\n  "value": ""\n}');
      const descriptionArContent = ref('{\n  "value": ""\n}');
      const logoContent = ref('');
      const thumbnailContent = ref('');

      const logosInput = ref<File[]>([]);
      const logoPreviews = ref<string[]>([]);
      const logoThumbnail = ref('/media/avatars/blank-image.jpg');

      const thumbnailsInput = ref<File[]>([]);
      const thumbnailPreviews = ref<string[]>([]);
      const thumbnailThumbnail = ref('/media/avatars/blank-image.jpg');

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const slugify = (text: string): string => {
        return text
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^\w-]+/g, '')
          .replace(/--+/g, '-')
          .replace(/^-+/, '')
          .replace(/-+$/, '');
      };

      const onBrokerAgentChange = () => {
        const brokerSlug = slugify(brokerName.value || '');
        const agentSlug = slugify(agentName.value || '');
        slug.value = `${brokerSlug}-${agentSlug}`.replace(/^-|-$/g, '');
      };

      const previewLogos = (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        if (files) {
          logosInput.value = Array.from(files);
          logoPreviews.value = [];

          for (let i = 0; i < files.length; i++) {
            const reader = new FileReader();
            reader.onload = (e) => {
              logoPreviews.value.push(e.target?.result as string);
              if (i === 0) {
                logoThumbnail.value = e.target?.result as string;
              }
            };
            reader.readAsDataURL(files[i]);
          }
        }
      };

      const previewThumbnails = (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        if (files) {
          thumbnailsInput.value = Array.from(files);
          thumbnailPreviews.value = [];

          for (let i = 0; i < files.length; i++) {
            const reader = new FileReader();
            reader.onload = (e) => {
              thumbnailPreviews.value.push(e.target?.result as string);
              if (i === 0) {
                thumbnailThumbnail.value = e.target?.result as string;
              }
            };
            reader.readAsDataURL(files[i]);
          }
        }
      };

      const removeLogo = (index: number) => {
        logoPreviews.value.splice(index, 1);

        const dt = new DataTransfer();
        const files = logosInput.value;

        for (let i = 0; i < files.length; i++) {
          if (i !== index) {
            dt.items.add(files[i]);
          }
        }

        logosInput.value = Array.from(dt.files);

        if (logoPreviews.value.length > 0) {
          logoThumbnail.value = logoPreviews.value[0];
        } else {
          logoThumbnail.value = '/media/avatars/blank-image.jpg';
        }
      };

      const removeThumbnail = (index: number) => {
        thumbnailPreviews.value.splice(index, 1);

        const dt = new DataTransfer();
        const files = thumbnailsInput.value;

        for (let i = 0; i < files.length; i++) {
          if (i !== index) {
            dt.items.add(files[i]);
          }
        }

        thumbnailsInput.value = Array.from(dt.files);

        if (thumbnailPreviews.value.length > 0) {
          thumbnailThumbnail.value = thumbnailPreviews.value[0];
        } else {
          thumbnailThumbnail.value = '/media/avatars/blank-image.jpg';
        }
      };

      const uploadImages = async (
        configId: number,
        files: File[],
        imageType: 'logo' | 'thumbnail'
      ) => {
        if (!files || files.length === 0) {
          return null;
        }

        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          formData.append('images', files[i]);
        }

        try {
          const response = await store.dispatch(
            Actions.UPLOAD_ADS_BROKERAGE_LOGO,
            {
              data: formData,
              id: configId,
              imageType,
            }
          );

          return response;
        } catch (error) {
          console.error(`${imageType} upload error:`, error);
          return null;
        }
      };

      const getUpdatedImageUrls = async (
        configId: number,
        imageType: 'logo' | 'thumbnail'
      ): Promise<string[]> => {
        try {
          const { data } = await store.dispatch(
            Actions.GET_ADS_BROKERAGE_CONFIG,
            configId
          );

          if (data.data && data.data[imageType]) {
            const imageJson = data.data[imageType];

            const urls = Object.keys(imageJson)
              .filter((key) => key.startsWith('value'))
              .map((key) => imageJson[key])
              .filter((url) => url);

            return urls;
          }

          return [];
        } catch (error) {
          console.error(`Error fetching updated ${imageType} URLs:`, error);
          return [];
        }
      };

      const configSchema = Yup.object().shape({
        slug: Yup.string()
          .required(() => translate('SLUG_IS_REQUIRED_FIELD'))
          .label('Slug'),
        brokerName: Yup.string()
          .required(() => translate('BROKER_NAME_IS_REQUIRED_FIELD'))
          .label('Broker Name'),
        agentName: Yup.string()
          .required(() => translate('AGENT_NAME_IS_REQUIRED_FIELD'))
          .label('Agent Name'),
        phone: Yup.string()
          .required(() => translate('PHONE_IS_REQUIRED_FIELD'))
          .label('Phone'),
      });

      const fireError = (errorMessage?: string) => {
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = errorMessage || store.getters.getErrors[errorName]?.[0];
        Swal.fire({
          text: translate(error || 'ERROR_OCCURRED'),
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: translate('tryAgainExcl'),
          customClass: {
            confirmButton: 'btn fw-bold btn-light-danger',
          },
        });
        submitButton.value?.removeAttribute('data-kt-indicator');
        submitButton.value!.disabled = false;
      };

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        try {
          await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');

          const titleEn = JSON.parse(titleEnContent.value);
          const titleAr = JSON.parse(titleArContent.value);
          const descriptionEn = JSON.parse(descriptionEnContent.value);
          const descriptionAr = JSON.parse(descriptionArContent.value);

          const dataToSubmit = {
            slug: values.slug,
            brokerName: values.brokerName,
            agentName: values.agentName,
            titleEn: titleEn,
            titleAr: titleAr,
            descriptionEn: descriptionEn,
            descriptionAr: descriptionAr,
            phone: values.phone,
          };

          const config = await store.dispatch(
            Actions.CREATE_ADS_BROKERAGE_CONFIG,
            dataToSubmit
          );

          if (!config?.id) {
            await store.dispatch(
              Actions.REMOVE_BODY_CLASSNAME,
              'page-loading-over'
            );
            fireError();
            return;
          }

          if (logosInput.value && logosInput.value.length > 0) {
            const uploadResponse = await uploadImages(
              config.id,
              logosInput.value,
              'logo'
            );

            if (uploadResponse) {
              const logoUrls = await getUpdatedImageUrls(config.id, 'logo');

              if (logoUrls.length > 0) {
                const logoJson: any = {};
                logoUrls.forEach((url, index) => {
                  logoJson[`value${index + 1}`] = url;
                });
                logoContent.value = JSON.stringify(logoJson, null, 2);
              }
            }
          }

          if (thumbnailsInput.value && thumbnailsInput.value.length > 0) {
            const uploadResponse = await uploadImages(
              config.id,
              thumbnailsInput.value,
              'thumbnail'
            );

            if (uploadResponse) {
              const thumbnailUrls = await getUpdatedImageUrls(
                config.id,
                'thumbnail'
              );

              if (thumbnailUrls.length > 0) {
                const thumbnailJson: any = {};
                thumbnailUrls.forEach((url, index) => {
                  thumbnailJson[`value${index + 1}`] = url;
                });
                thumbnailContent.value = JSON.stringify(thumbnailJson, null, 2);
              }
            }
          }

          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );

          Swal.fire({
            text: translate('SUCCESSFULLY_CREATED_ADS_BROKERAGE_CONFIG'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'ads-brokerage-configuration-listing' });
          });
        } catch (e) {
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );
          console.error('Error:', e);
          Swal.fire({
            text: translate('ERROR_PROCESSING_DATA'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        }
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('adsBrokerageConfigCreating'), [
          translate('ads'),
        ]);
      });

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitCreate,
        submitButton,
        translate,
        configSchema,
        goBack,
        can,
        titleEnContent,
        titleArContent,
        descriptionEnContent,
        descriptionArContent,
        logoContent,
        thumbnailContent,
        logosInput,
        logoPreviews,
        logoThumbnail,
        thumbnailsInput,
        thumbnailPreviews,
        thumbnailThumbnail,
        previewLogos,
        previewThumbnails,
        removeLogo,
        removeThumbnail,
        agentName,
        brokerName,
        slug,
        onBrokerAgentChange,
      };
    },
  });
